import type { ReactElement } from 'react';

/* A row of mutually-exclusive filter buttons, e.g. "All / Anomalous / Normal / …".
 *
 * **Not `Tabs`, and the difference is semantic.** `Tabs` implements the ARIA tab pattern — a strip
 * that owns one tab stop and controls a `tabpanel`. A filter switches which rows of one table are
 * shown; there is no panel. So this is a `role="group"` of buttons carrying `aria-pressed`, which is
 * the honest markup for "pick one of these to narrow what you see".
 *
 * Generic over the option id so a screen's filter union is checked at the call site — a `value` that
 * is not one of the `options` is a compile error rather than a strip with nothing pressed.
 */

export interface SegmentOption<Id extends string> {
  readonly id: Id;
  readonly label: string;
  /** A count beside the label. `undefined` shows nothing; `0` shows a zero — a real answer. */
  readonly count?: number | undefined;
}

export interface SegmentedFilterProps<Id extends string> {
  readonly options: readonly SegmentOption<Id>[];
  readonly value: Id;
  readonly onChange: (id: Id) => void;
  /** Names the group for assistive technology, e.g. "Filter findings by verdict". */
  readonly label: string;
  /** Which paint: the kit's rounded chip, or the reference design's squared, inverted one.
   *
   * **A variant rather than a change to the default**, because the two are used on screens built to
   * two different moments of this design system and the reference's own paint is not more correct in
   * the abstract — it is more correct *on a page whose every other edge is square*. Changing the
   * default would silently re-paint the anomaly screen's verdict filter to match a page it does not
   * sit beside. `'ledger'` names what it belongs to: the framed, radius-free table variant. */
  readonly variant?: 'default' | 'ledger' | undefined;
}

export function SegmentedFilter<Id extends string>({
  options,
  value,
  onChange,
  label,
  variant = 'default',
}: SegmentedFilterProps<Id>): ReactElement {
  return (
    <div
      className={
        variant === 'ledger' ? 'lq-segfilter lq-segfilter--ledger' : 'lq-segfilter'
      }
      role="group"
      aria-label={label}
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className="lq-segfilter__option"
          aria-pressed={option.id === value}
          onClick={() => {
            onChange(option.id);
          }}
        >
          {option.label}
          {option.count !== undefined && (
            <span className="lq-segfilter__count">{option.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}
